<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <card-page :card-title="cardTitle">
          <vue-element-loading :active="busy"></vue-element-loading>
          <vue-form-generator
              ref="form"
              :model="model"
              :options="formOptions"
              :schema="schema"
              :class="readonly ? 'readonly-form' : ''"
          />
          <additive-widget
            ref="formPlacingBrokers"
            v-model="model"
            :form-options="formOptions"
            :schema="schemaPlacingBrokers"
            label="Taxes"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from '@/components/mixins/FormModelMixin';
import FormFieldValidationMixin from "@/components/mixins/FormFieldValidationMixin";
import {selectOptions, datepickerOptions, arrayOptions} from '@/components/form/FormElementOption';
import AdditiveWidget from '@/components/form/fields/AdditiveWidget';
import moment from 'moment';
import CardPage from '../../../components/Views/CardPage';

export default {
  name: 'SectionForm',
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  components: {
    AdditiveWidget,
    CardPage,
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],
  data() {
    return {
      selectedReferences: [],
      model: {
        Section: null,
        Description: null,
        InceptionDate: null,
        ExpiryDate: null,
        WholesaleCharge_PC: null,
        ClassOfRisk_FK: {},
        SectionClassificationType_FK: null,
        IsWholeSaleChargeDeductible: 0,
        Accessories: null,
        Taxes: null,
        CapacityLimit: null,
        OriginalSigningNumberDate: null,
        CapacityLimit_PC: null,
        Note: null,
        LIOLevy_PC: null,
        tax: [],
      },
      schema: {
        groups: [
          {
            styleClasses: 'w-full',
            legend: 'General',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Section',
                model: 'Section',
                validator: 'string',
                required: () => !this.readonly,
                max: 10,
              },
              {
                disabled: () => this.readonly,
                type: 'textArea',
                inputType: 'textArea',
                styleClasses: 'vx-col w-1/3',
                label: 'Description',
                model: 'Description',
                required: () => !this.readonly,
                validator: 'string',
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Class Of Risk',
                model: 'ClassOfRisk_FK',
                values: () => this.classOfRisk,
                selectOptions,
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Section Classification Type',
                model: 'SectionClassificationType_FK',
                values: () => this.sectionClassificationType,
                selectOptions,
                required: () => !this.readonly,
                validator: 'required'
              },
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Dates',
            fields: [
              {
                ...datepickerOptions,
                disabled: () => this.readonly,
                styleClasses: 'vx-col w-1/3',
                label: 'Inception Date',
                model: 'InceptionDate',
                required: () => !this.readonly,
                onChanged: () => {
                  this.validateField('ExpiryDate');
                },
                validator: ['required', (value, field, model) => {
                  if (model.ExpiryDate) {
                    let expiryDate = model.ExpiryDate;
                    let inceptionDate = moment(model.InceptionDate).unix();

                    if (typeof expiryDate === 'string') {
                      expiryDate = moment(model.ExpiryDate, "YYYY-MM-DD").unix();
                    } else {
                      expiryDate = expiryDate / 1000;
                    }

                    if (inceptionDate > expiryDate) {
                      return 'Inception Date must be before Expiry Date.';
                    }
                  }

                  return true;
                }],
              },
              {
                disabled: () => this.readonly,
                ...datepickerOptions,
                styleClasses: 'vx-col w-1/3',
                label: 'Expiry Date',
                model: 'ExpiryDate',
                required: () => !this.readonly,
                onChanged: () => {
                  this.validateField('InceptionDate');
                },
                validator: ['string', (value, field, model) => {
                  if (model.InceptionDate) {
                    let inceptionDate = model.InceptionDate;
                    let expiryDate = moment(model.ExpiryDate).unix();

                    if (typeof inceptionDate === 'string') {
                      inceptionDate = moment(model.InceptionDate, "YYYY-MM-DD").unix();
                    } else {
                      inceptionDate = inceptionDate / 1000;
                    }

                    if (expiryDate < inceptionDate) {
                      return 'Expiry Date must be after Inception Date.';
                    }
                  }

                  return true;
                }],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Figures',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Section IL Amount',
                model: 'CapacityLimit',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric'],
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Section IL % ',
                model: 'CapacityLimit_PC',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric'],
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'LIO Levy %',
                model: 'LIOLevy_PC',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric'],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Wholesale Charge %',
                model: 'WholesaleCharge_PC',
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric'],
              },
              {
                disabled: () => this.readonly,
                type: 'vsCheckbox',
                styleClasses: 'vx-col w-1/3',
                label: 'Is Wholesale Charge Deductible',
                model: 'IsWholeSaleChargeDeductible',
                required: () => !this.readonly,
                onChanged: (model, schema, event, instance) => {
                  model.IsWholeSaleChargeDeductible = Number(model.IsWholeSaleChargeDeductible);
                },
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Taxes % ',
                model: 'Taxes',
                max: 100,
                min: 0,
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric', (value) => {
                  if (value > 100) {
                    return 'Value must be lower than 100';
                  }

                  return true;
                }],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Accessories % ',
                model: 'Accessories',
                max: 100,
                min: 0,
                required: () => !this.readonly,
                validator: ['required', 'positive', 'numeric', (value) => {
                  if (value > 100) {
                    return 'Value must be lower than 100';
                  }

                  return true;
                }],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'SLA',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'OTP Expire Time',
                model: 'OTPExpireTime',
                max: 100,
                min: 0,
                validator: [(value) => {
                  // Can be empty
                  if (!value) {
                    return true;
                  }

                  if (typeof value !== 'number') {
                    return 'Value must be a number';
                  }

                  if (value < 0) {
                    return 'Value must be greater than 0';
                  }

                  return true;
                }],
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'OTP First Reminder',
                model: 'OTPFirstReminder',
                max: 100,
                min: 0,
                validator: [(value) => {
                  // Can be empty
                  if (!value) {
                    return true;
                  }

                  if (typeof value !== 'number') {
                    console.log(value);
                    return 'Value must be a number';
                  }

                  if (value < 0) {
                    return 'Value must be greater than 0';
                  }

                  return true;
                }],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Notes',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'textArea',
                inputType: 'textarea',
                styleClasses: 'vx-col w-1/3',
                label: 'Note',
                model: 'Note',
              },
            ]
          },
        ],
      },
      schemaPlacingBrokers: {
        groups: [
          {
            styleClasses: 'w-full',
            legend: 'Tax',
            fields: [
              {
                model: 'tax',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'input',
                            inputType: 'text',
                            styleClasses: 'vx-col w-1/3',
                            label: 'Tax Identifier',
                            model: 'TaxIdentifier',
                            required: () => !this.readonly,
                            validator: ['required', 'string'],
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'input',
                            inputType: 'number',
                            styleClasses: 'vx-col w-1/3',
                            label: 'Tax Amount %',
                            model: 'TaxAmount_PC',
                            required: () => !this.readonly,
                            validator: ['required', 'positive', 'numeric', (value) => {
                              if (value > 100) {
                                return 'Value must be lower than 100';
                              }

                              return true;
                            }],
                          },
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      classOfRisk: [],
      sectionClassificationType: [],
      options: [
        {
          endpoint: () => this.$api.facilityClassOfRisk.list(),
          optionsFieldName: 'classOfRisk',
          nameFieldName: 'FullName',
          idFieldName: 'ClassOfRisk_FK',
        },
        {
          endpoint: () => this.$api.facilitySectionClassificationType.list(),
          optionsFieldName: 'sectionClassificationType',
          nameFieldName: 'Type',
          idFieldName: 'SectionClassificationType_FK',
        },
      ]
    };
  },
};
</script>
